import firebase from 'firebase/compat/app';
import { fetchUsers } from '../../services/usersServices';
import { getTime } from '../../services/projects-services';

// Fetch projects from Firestore
export const fetchProjects = async () => {
  const db = firebase.firestore();
  const projectsSnapshot = await db.collection('projects').get();

  // Directly map to a reduced structure
  const projectsMap = projectsSnapshot.docs.reduce((acc, doc) => {
    const data = doc.data();
    acc[doc.id] = {
      state: data.state,
      isNonBillable: data.isNonBillable,
    };
    return acc;
  }, {});

  return projectsMap;
};

export const fetchUsersTime = async (startDate, endDate) => {
  const usersData = await fetchUsers();
  const data = await getTime();
  const allTimeEntries = [];

  data.forEach((userTime) => {
    const { weeklyHours } = userTime;

    const userDetails = usersData.find(
      (user) =>
        `${user.firstName}_${user.surname}`.replace(/\s+/g, '_').toLowerCase() === userTime.id,
    );
    if (userDetails?.employmentStatus === 'Contract') {
      return;
    }
    const fullName = userDetails?.fullName || userTime.id;
    const serviceGroup = userDetails?.['serviceGroup'] || 'Unknown';

    Object.entries(weeklyHours || {}).forEach(([weekStarting, projects]) => {
      const weekDate = new Date(weekStarting);

      if (weekDate >= new Date(startDate) && weekDate <= new Date(endDate)) {
        projects.forEach((project) => {
          // **Filter out Paid Personal Leave projects**
          if (project.flowId === '20015' || project.name === 'FKA Paid Personal Leave') {
            return;
          }
          const filteredHours = project.hours.filter((hour) => {
            const [day, month, year] = hour.date.split('-');
            const entryDate = new Date(`${year}-${month}-${day}`);
            return entryDate >= new Date(startDate) && entryDate <= new Date(endDate);
          });

          // Aggregate hours directly during fetch
          const totalHours = filteredHours.reduce(
            (acc, { hours: hourValue, isNonBillable }) => {
              const parsedHours = parseFloat(hourValue || 0);
              if (isNonBillable) {
                acc.nonBillable += parsedHours;
              } else {
                acc.billable += parsedHours;
              }
              return acc;
            },
            { billable: 0, nonBillable: 0 },
          );

          if (totalHours.billable > 0 || totalHours.nonBillable > 0) {
            allTimeEntries.push({
              serviceGroup,
              userName: fullName,
              flowId: project.flowId,
              billableHours: totalHours.billable,
              nonBillableHours: totalHours.nonBillable,
            });
          }
        });
      }
    });
  });

  return allTimeEntries;
};

// Process time entries into departments and members structure
export const processTimeEntries = (timeEntries, projectsMap) => {
  const parsedTimeEnteries = timeEntries.reduce(
    (acc, { serviceGroup, userName, flowId, billableHours, nonBillableHours }) => {
      if (!acc[serviceGroup]) {
        acc[serviceGroup] = {
          members: {},
          totalBillableHours: 0,
          totalNonBillableHours: 0,
          isActive: true,
        };
      }

      const dept = acc[serviceGroup];

      if (!dept.members[userName]) {
        dept.members[userName] = {
          name: userName
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
          username: userName,
          billableHours: 0,
          nonBillableHours: 0,
          isActive: true,
        };
      }

      const member = dept.members[userName];
      const project = projectsMap[flowId] || {};
      const isNonBillable =
        project.isNonBillable || ['Opportunity', 'Committed'].includes(project.state);

      // Update department and member totals directly
      if (isNonBillable) {
        member.nonBillableHours += billableHours + nonBillableHours; // Ensure both are counted
        dept.totalNonBillableHours += billableHours + nonBillableHours;
      } else {
        member.billableHours += billableHours;
        dept.totalBillableHours += billableHours;
        member.nonBillableHours += nonBillableHours;
        dept.totalNonBillableHours += nonBillableHours;
      }
      return acc;
    },
    {},
  );
  return parsedTimeEnteries;
};

export const formatNumber = (number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

export const calculatePercentage = (partialValue, totalValue) => {
  if (!totalValue || totalValue <= 0) return '0.0';
  return ((partialValue / totalValue) * 100).toFixed(1);
};

// Calculate total billable and non-billable hours
export const calculateTotals = (departmentData) => {
  return Object.values(departmentData).reduce(
    (totals, dept) => {
      if (dept.isActive) {
        Object.values(dept.members).forEach((member) => {
          if (member.isActive) {
            totals.totalBillable += member.billableHours;
            totals.totalNonBillable += member.nonBillableHours;
          }
        });
      }
      return totals;
    },
    { totalBillable: 0, totalNonBillable: 0 },
  );
};
